import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Clock, Users, ShoppingCart, Play } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';

const CourseCard = ({ course, viewMode = 'grid' }) => {
  const { addToCart, isInCart } = useCart();
  const { user, isAuthenticated } = useAuth();

  // Check if user is enrolled in this course
  const isEnrolled = isAuthenticated && user?.enrolledCourses?.some(
    enrollment => (enrollment.course && enrollment.course._id === course._id) || enrollment.course === course._id
  );

  // Get enrollment data for resume link
  const enrollment = isAuthenticated && user?.enrolledCourses?.find(
    enrollment => (enrollment.course && enrollment.course._id === course._id) || enrollment.course === course._id
  );

  // Build resume link with lastViewedLesson query params
  const getResumeLink = () => {
    const baseLink = `/courses/${course._id}/learn`;
    if (enrollment?.lastViewedLesson) {
      const { unitIndex, lessonIndex } = enrollment.lastViewedLesson;
      return `${baseLink}?unitIndex=${unitIndex}&lessonIndex=${lessonIndex}`;
    }
    return baseLink;
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(course);
  };

  // Get creator info safely
  const creator = course.createdBy || course.instructor;
  const creatorName = creator?.name || 'Unknown';
  const creatorAvatar = creator?.avatar || '/images/default-avatar.svg';

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex space-x-6">
          {/* Course Image */}
          <div className="flex-shrink-0">
            <img
              src={course.thumbnail}
              alt={course.title}
              className="w-48 h-32 object-cover rounded-lg"
            />
          </div>

          {/* Course Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  <Link to={`/courses/${course._id}`} className="hover:text-primary-600">
                    {course.title}
                  </Link>
                </h3>
                <p className="text-gray-600 mb-3">{course.subtitle}</p>
                
                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{course.totalDuration}시간</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    <span>{course.enrolledStudents.toLocaleString()}명 수강</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="w-4 h-4 mr-1 text-yellow-400" />
                    <span>{course.averageRating} ({course.totalReviews}개 리뷰)</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <img
                      src={creatorAvatar}
                      alt={creatorName}
                      className="w-8 h-8 rounded-full"
                    />
                    <span className="text-sm text-gray-600">{creatorName}</span>
                  </div>
                </div>
              </div>

              {/* Price and Action */}
              <div className="flex flex-col items-end space-y-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    ₩{course.price.toLocaleString()}
                  </div>
                  {course.originalPrice > course.price && (
                    <div className="text-sm text-gray-500 line-through">
                      ₩{course.originalPrice.toLocaleString()}
                    </div>
                  )}
                </div>
                
                {isEnrolled ? (
                  <Link
                    to={getResumeLink()}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    계속 학습하기
                  </Link>
                ) : (
                  <button
                    onClick={handleAddToCart}
                    disabled={isInCart(course._id)}
                    className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {isInCart(course._id) ? '장바구니에 추가됨' : '장바구니에 추가'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Course Image */}
      <div className="relative">
        <img
          src={course.thumbnail}
          alt={course.title}
          className="w-full h-48 object-cover"
        />
        {course.isFeatured && (
          <div className="absolute top-2 left-2 bg-primary-600 text-white px-2 py-1 rounded text-xs font-medium">
            추천
          </div>
        )}
      </div>

      {/* Course Content */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
          <Link to={`/courses/${course._id}`} className="hover:text-primary-600">
            {course.title}
          </Link>
        </h3>
        
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{course.subtitle}</p>

        {/* Creator */}
        <div className="flex items-center space-x-2 mb-3">
          <img
            src={creatorAvatar}
            alt={creatorName}
            className="w-6 h-6 rounded-full"
          />
          <span className="text-sm text-gray-600">{creatorName}</span>
        </div>

        {/* Course Stats */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <div className="flex items-center">
            <Star className="w-4 h-4 mr-1 text-yellow-400" />
            <span>{course.averageRating}</span>
            <span className="ml-1">({course.totalReviews})</span>
          </div>
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            <span>{course.totalDuration}시간</span>
          </div>
        </div>

        {/* Price and Action */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-bold text-gray-900">
              ₩{course.price.toLocaleString()}
            </div>
            {course.originalPrice > course.price && (
              <div className="text-sm text-gray-500 line-through">
                ₩{course.originalPrice.toLocaleString()}
              </div>
            )}
          </div>
          
          {isEnrolled ? (
            <Link
              to={getResumeLink()}
              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              <Play className="w-4 h-4 mr-1" />
              학습하기
            </Link>
          ) : (
            <button
              onClick={handleAddToCart}
              disabled={isInCart(course._id)}
              className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <ShoppingCart className="w-4 h-4 mr-1" />
              {isInCart(course._id) ? '추가됨' : '추가'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseCard; 